import type { TemperatureSeries } from "../types";

export type ChartDatum = {
  hours: number;
  timestamp: string | null;
  [key: string]: number | string | null;
};

export function buildTemperatureChartData(series: TemperatureSeries[]): ChartDatum[] {
  const lookup = new Map<string, ChartDatum>();

  series.forEach((serie) => {
    const serieId = seriesKey(serie);
    serie.points.forEach((point) => {
      const identifier = point.timestamp ?? String(point.hours);
      const existing = lookup.get(identifier);
      if (existing) {
        existing[serieId] = point.value;
      } else {
        lookup.set(identifier, {
          hours: point.hours,
          timestamp: point.timestamp,
          [serieId]: point.value,
        });
      }
    });
  });

  return Array.from(lookup.values()).sort((a, b) => a.hours - b.hours);
}

export function computeTemperatureDomain(series: TemperatureSeries[], safeRange: { min: number; max: number }) {
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;

  series.forEach((serie) => {
    serie.points.forEach((point) => {
      if (Number.isFinite(point.value)) {
        min = Math.min(min, point.value);
        max = Math.max(max, point.value);
      }
    });
  });

  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    return [safeRange.min - 5, safeRange.max + 5] as const;
  }

  const spread = Math.max(max - min, 1);
  const padding = Math.max(spread * 0.1, 0.5);

  return [Math.floor(min - padding), Math.ceil(max + padding)] as const;
}

export function seriesKey(serie: TemperatureSeries) {
  return `${serie.shipmentId}::${serie.deviceId}`;
}

export function formatDateLabel(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("fr-FR", { month: "2-digit", day: "2-digit" });
}

export function formatTooltipDate(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("fr-FR", { month: "long", day: "numeric" });
}

